import React, { useEffect, useMemo, useState } from "react";
import {
    Autosuggest,
    Box,
    ColumnLayout,
    Container,
    FormField,
    Header,
    Link,
    SpaceBetween,
    Table,
} from "@amzn/awsui-components-react";
import ServiceCollection from "src/services/ServiceCollection";
import apiService from "src/services/ApiCallService";
import {
    TestedPartyMapping,
    VersionedTestedPartyMapping,
} from "src/models/tested-party-mapping/TestedPartyMapping";
import { sourceBadge, versionColumnDefinitions, TESTED_PARTY, LATEST_VERSION_KEY, SYSTEM_DEFAULT } from "./Constants";

/** Displays version history for a selected CLI's Tested Party mapping via autosuggest selection. */
export default function AuditHistoryTab(props: { services: ServiceCollection }) {
    const [cliInput, setCliInput] = useState("");
    const [selectedCli, setSelectedCli] = useState<string | null>(null);
    const [versions, setVersions] = useState<VersionedTestedPartyMapping[]>([]);
    const [loading, setLoading] = useState(false);
    const [allCliOptions, setAllCliOptions] = useState<{ value: string }[]>([]);

    // Populate the CLI dropdown suggestions — only CLIs whose mapping has been changed
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await apiService.getAllCliReportingConfig(TESTED_PARTY);
                if (!cancelled) {
                    const options = (response?.CliReportingConfigs ?? [])
                        .filter((m: TestedPartyMapping) => m.source !== SYSTEM_DEFAULT)
                        .map((m: TestedPartyMapping) => ({
                            value: m.cliNumber,
                        }));
                    setAllCliOptions(options);
                }
            } catch {
                if (!cancelled) {
                    props.services.messageService.showErrorAutoDismissBanner("Failed to load CLI options");
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Fetch version history when a CLI is selected
    useEffect(() => {
        if (!selectedCli) {
            setVersions([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const response = await apiService.getCliReportingConfig(selectedCli, TESTED_PARTY);
                const allConfigs = response?.CliReportingConfigs ?? [];

                const mapped: VersionedTestedPartyMapping[] = allConfigs.map((config: any, index: number) => {
                    const versionPart = config.version?.split("#").pop() ?? `v${index}`;
                    const isV0 = versionPart === "v0";
                    return {
                        ...config,
                        isLatest: isV0,
                        versionKey: isV0 ? "latest" : versionPart,
                    };
                });

                if (!cancelled) setVersions(mapped);
            } catch {
                if (!cancelled) {
                    props.services.messageService.showErrorAutoDismissBanner("Failed to load audit history");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedCli]);

    const latestVersion = versions[0];
    const amendedFromCli = latestVersion?.amendedFromCliNumber;

    const sortedVersions = useMemo(
        () => [...versions].sort((a, b) => b.currentVersion - a.currentVersion),
        [versions]
    );

    return (
        <SpaceBetween direction="vertical" size="m">
            <Container header={<Header variant="h3">Select CLI</Header>}>
                <FormField
                    label="CLI Number"
                    description="Select a CLI to view its Tested Party mapping version history"
                >
                    <Autosuggest
                        filteringType="auto"
                        value={cliInput}
                        options={allCliOptions}
                        onChange={({ detail }) => setCliInput(detail.value)}
                        onSelect={({ detail }) => {
                            setCliInput(detail.value);
                            setSelectedCli(detail.value);
                        }}
                        onBlur={() => {
                            if (cliInput && allCliOptions.some((o) => o.value === cliInput)) {
                                setSelectedCli(cliInput);
                            }
                        }}
                        placeholder="Enter or select a CLI number"
                        enteredTextLabel={(v) => `Use: "${v}"`}
                        empty="No matching CLIs"
                    />
                </FormField>
            </Container>

            {selectedCli && latestVersion && (
                <Container
                    header={
                        <Header
                            variant="h3"
                            description={
                                amendedFromCli ? (
                                    <>
                                        This CLI was amended from{" "}
                                        <Link
                                            onFollow={(e) => {
                                                e.preventDefault();
                                                setCliInput(amendedFromCli);
                                                setSelectedCli(amendedFromCli);
                                            }}
                                        >
                                            {amendedFromCli}
                                        </Link>
                                    </>
                                ) : undefined
                            }
                        >
                            {selectedCli}
                        </Header>
                    }
                >
                    <ColumnLayout columns={3} variant="text-grid">
                        <div>
                            <Box variant="awsui-key-label">Agreement</Box>
                            {latestVersion.agreementNumber} — {latestVersion.agreementName}
                        </div>
                        <div>
                            <Box variant="awsui-key-label">First Party</Box>
                            {latestVersion.firstPartyEntityName} ({latestVersion.firstPartyCompanyCode})
                        </div>
                        <div>
                            <Box variant="awsui-key-label">Second Party</Box>
                            {latestVersion.secondPartyEntityName} ({latestVersion.secondPartyCompanyCode})
                        </div>
                    </ColumnLayout>
                </Container>
            )}

            {selectedCli && (
                <Table
                    header={
                        <Header counter={sortedVersions.length ? `(${sortedVersions.length})` : undefined}>
                            Version History
                        </Header>
                    }
                    items={sortedVersions}
                    columnDefinitions={versionColumnDefinitions}
                    loading={loading}
                    loadingText="Loading audit history"
                    trackBy="versionKey"
                    empty={
                        <Box textAlign="center" padding="m">
                            No version history available for this CLI
                        </Box>
                    }
                    variant="container"
                />
            )}
        </SpaceBetween>
    );
}
